import React, { useContext } from 'react';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { BareContentTemplateLegacy } from '@go-shared/types/contentTemplate';
import { FeedEditorHotspot } from '@go-client/components';
import { FeedArea } from '@go-shared/types/feedTemplate';
import { Plan } from '@go-shared/types/plan';
import { DEFAULT_TIMESTR_FORMAT, minutesToTimeString, timeToMinutes } from '@go-shared/lib/time';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { getVenueLocation } from '@go-shared/lib/venue';
import { Language } from '@go-shared/types/info';
import { makeBareEventSorter } from '@go-shared/lib/event';
import { getHourFormat } from '@go-shared/lib/property';
import { translateMealPeriod } from '@go-shared/lib/mealPeriods';
import { MealPeriodTranslation } from '@go-shared/types/translation';
import { mapBy } from '@go-shared/lib/functional';
import { BrandCommonTextValues } from '@go-shared/types/brand';

interface VenueMealPeriodsHotspotProps {
  contentTemplate: BareContentTemplateLegacy;
  area: FeedArea;
  selectedLanguage: Language | undefined;
  showMergedVars: boolean;
  highlightAreas: boolean;
  onDrop?: (contentTemplate: BareContentTemplateLegacy) => void;
  onAreaDeleted: (area: FeedArea) => void;
  editorLocked: boolean;
  commonTexts: BrandCommonTextValues;
  mcmKey: string;
}

export const VenueMealPeriodsHotspot: React.FC<VenueMealPeriodsHotspotProps> = ({
  contentTemplate,
  area,
  onDrop,
  onAreaDeleted,
  selectedLanguage,
  showMergedVars,
  highlightAreas,
  editorLocked,
  commonTexts,
  mcmKey,
}) => {
  const { plan, venueTemplates, settingsPanel, mpTranslations } = useContext(FeedEditorCtx);
  const contentTemplateReady: BareContentTemplateLegacy = {
    ...contentTemplate,
    content: buildContentForVenueMealPeriods(
      area.content!.params!.venueMealPeriodsConfig!,
      venueTemplates,
      plan!,
      mpTranslations,
      commonTexts,
      selectedLanguage
    ),
  };
  if (!area.style) area.style = {};
  const isSelected =
    settingsPanel?.type === 'venue-meal-periods-settings' &&
    area.content!._id === settingsPanel.venueMealPeriodsId;
  (area.style as any).width = '100%';
  (area.style as any).height = area.content!.params!.venueMealPeriodsConfig!.length ? 'auto' : 100;
  area.className = 'relative';

  return (
    <div
      className={`venue-meal-periods relative w-full ${
        isSelected ? 'outline-venue-meal-periods' : ''
      } rounded`}
    >
      <FeedEditorHotspot
        onAreaDeleted={onAreaDeleted}
        highlightAreas={highlightAreas}
        onElementCreated={() => null}
        onElementChanged={() => null}
        onColumnCreated={() => null}
        onRowMove={() => null}
        onRowCreated={() => null}
        onContentChange={() => null}
        contentTemplate={contentTemplateReady}
        key={area.content!._id}
        area={area}
        onDrop={onDrop}
        selectedLanguage={selectedLanguage}
        showMergedVars={showMergedVars}
        editorLocked={editorLocked}
        mcmKey={mcmKey}
      />
    </div>
  );
};

const buildContentForVenueMealPeriods = (
  ids: string[],
  vts: VenueTemplate[],
  plan: Plan,
  mpTranslations: MealPeriodTranslation[],
  commonTexts: BrandCommonTextValues,
  lang?: Language
) => {
  let content = '';
  ids &&
    ids.forEach(id => {
      const venueData = plan.venueData.find(vd => vd.template === id);
      const dayStartMinutes = timeToMinutes(plan.dayStart);
      const template = vts.find(vt => vt._id === id);
      const hourFormat = getHourFormat(plan.property);

      if (template) {
        let venueDisplayName = venueData?.displayOptions?.displayName || template.name;
        let venueLevelName = venueData?.displayOptions?.deckDisplayName || String(template.level);
        if (venueLevelName.trim() !== '') {
          venueLevelName += ` ${getVenueLocation(template, commonTexts)}`;

          const deckLabel = `<span class="venue-meal-periods-deck-separator">, </span><span class="venue-meal-periods-deck-name">${commonTexts.deck}</span> ${venueLevelName}`;

          // Add the venue next to the name, but hide by default
          venueDisplayName += `<span class="venue-meal-periods-inline-deck" style="display: none">${deckLabel}</span>`;
        }

        if (venueData && venueData.mealPeriods && venueData.mealPeriods.length > 0) {
          content += `<div class="venue-meal-periods-row table-row w-full">`;
          content += `<div class="venue-meal-periods-venue-name table-cell"><p>${venueDisplayName}<p></div>`;

          content += '<div class="venue-meal-periods-times table-cell whitespace-no-wrap">';
          let mealPeriodSorter = makeBareEventSorter(dayStartMinutes);
          let mpTranslationsMap = mapBy(mpTranslations, 'refers');
          let mealPeriods = venueData.mealPeriods.map(
            mp => translateMealPeriod(mp, mpTranslationsMap[mp.brandMealPeriodId], lang?.key) || mp
          );
          mealPeriods.sort(mealPeriodSorter);

          let hoursContent = mealPeriods.map(mp => {
            let startStr = minutesToTimeString(
              timeToMinutes(mp.startTime),
              hourFormat,
              DEFAULT_TIMESTR_FORMAT
            );
            let endStr = minutesToTimeString(
              timeToMinutes(mp.endTime),
              hourFormat,
              DEFAULT_TIMESTR_FORMAT
            );

            return `<p class="mr-5">${startStr} - ${endStr}</p>`;
          });

          content += `<div class="venue-meal-periods-times-hours">${hoursContent.join('\n')}</div>`;

          let periodContent = mealPeriods.map(mp => `<p class="mr-5">${mp.name}</p>`);
          content += `<div class="venue-meal-periods-times-names">${periodContent.join(
            '\n'
          )}</div>`;

          content += '</div>'; // venue-meal-periods-times
          content += '</div>'; // venue-meal-periods-row
        }
      }
    });
  return `<div>${content}</div`;
};
